import { defineMetrics } from "../types.js"

/**
 * ⚠ `reports_by_module`'s `dims.module` is a **report feature name** (`TRACKING_REPORTS` and
 * friends), **not** the `userBehaviorAnalysis.module` vocabulary that `moduleUsage` uses. The two
 * must never be joined on `dims.module` — see the note in module-usage for what happens when the
 * two module vocabularies collide.
 */
export const reportsUsageMetrics = defineMetrics([
  {
    id: "reports_generated",
    name: "Reports Generated",
    description: "Number of reports generated within a module.",
    logic: "Count report generation events.",
    source: "queue(actionName in *_REPORTS, status=done).createdAt — not the Report collections",
    domain: "reportsUsage",
    srvModule: "reportsUsage",
    priority: "must",
    valueType: "count",
    dims: [],
    rollup: "sum",
    defaultWidget: "stat-tile",
    altWidgets: ["line", "bar"],
    coveredByV1: true,
    caveat:
      "Read from the task queue, whose rows are removed by a 3-day TTL index. Daily collection accumulates the history in our own store, but backfilling further back than three days will return near-zero without any error, and a collection outage longer than three days loses that period for good. Counts only generations that finished; a failed generation produced no report.",
    xlsxRow: 48,
  },
  {
    id: "report_type",
    name: "Report Type",
    description: "Type of report generated.",
    logic: "Retrieve report template/type used.",
    source: "queue.typeName, srv's own display name for the report",
    domain: "reportsUsage",
    srvModule: "reportsUsage",
    priority: "must",
    valueType: "count",
    dims: ["class"],
    rollup: "last",
    defaultWidget: "pie",
    altWidgets: ["hbar", "bar", "table"],
    coveredByV1: false,
    xlsxRow: 49,
  },
  {
    id: "report_generated_by",
    name: "Generated By",
    description: "User who generated the report.",
    logic: "Retrieve report creator information.",
    source: "queue.creator, pseudonymised",
    domain: "reportsUsage",
    srvModule: "reportsUsage",
    priority: "must",
    valueType: "count",
    dims: ["user"],
    rollup: "sum",
    defaultWidget: "hbar",
    altWidgets: ["bar", "table"],
    coveredByV1: false,
    caveat:
      "Equivalent in meaning to reports_by_user. The dictionary lists them as two separate rows, so both are kept here to stay aligned with the dictionary rather than merged.",
    xlsxRow: 50,
  },
  {
    id: "report_generation_date",
    name: "Report Generation Date",
    description: "Date and time the report was generated.",
    logic: "Retrieve report generation timestamp.",
    source: "queue.createdAt, one row per generated report",
    domain: "reportsUsage",
    srvModule: "reportsUsage",
    priority: "must",
    valueType: "timestamp",
    dims: ["entity"],
    rollup: "max",
    defaultWidget: "table",
    coveredByV1: false,
    xlsxRow: 51,
  },
  {
    id: "report_countries_selected",
    name: "Countries Selected (DeviceReg/PharmaReg)",
    description: "Countries selected during searches or report generation.",
    logic: "Record selected countries for each activity.",
    source: "queue.requestData.body.state.countries and .country, projected individually",
    domain: "reportsUsage",
    srvModule: "reportsUsage",
    priority: "nice",
    valueType: "count",
    dims: ["country"],
    rollup: "sum",
    defaultWidget: "hbar",
    altWidgets: ["map", "table"],
    coveredByV1: true,
    caveat:
      "Read from two named paths inside the queued request payload. That payload holds the entire inbound request, including the Authorization header and the full user document, so only those two paths are ever projected. Reports take either a country list or a single country depending on the report.",
    xlsxRow: 52,
  },
  {
    id: "report_sections_selected",
    name: "Sections Selected (DeviceReg/PharmaReg)",
    description: "Regulatory sections selected by users.",
    logic: "Record selected sections during searches or report generation.",
    source: "none — no report puts sections in its request state",
    domain: "reportsUsage",
    srvModule: "reportsUsage",
    priority: "nice",
    valueType: "count",
    dims: ["entity"],
    rollup: "sum",
    defaultWidget: "hbar",
    altWidgets: ["bar", "table"],
    coveredByV1: true,
    caveat:
      "Reported unsupported. No report in srv puts sections in its request state — every report module selects countries, products, SKUs or submission types. Sections are an application concept, which is why srv has an Export Application Section action and no report equivalent. Tracked as T-26.",
    xlsxRow: 53,
  },
  {
    id: "most_used_reports",
    name: "Most Frequently Used Reports",
    description: "Reports generated most often.",
    logic: "Rank reports by generation count.",
    source: "queue.typeName, ranked by generation count",
    domain: "reportsUsage",
    srvModule: "reportsUsage",
    priority: "nice",
    valueType: "count",
    dims: ["entity"],
    rollup: "sum",
    defaultWidget: "hbar",
    altWidgets: ["bar", "table"],
    coveredByV1: false,
    xlsxRow: 88,
  },
  {
    id: "reports_by_user",
    name: "Reports by User",
    description: "Reports generated by each user.",
    logic: "Group report generation events by user.",
    source: "createdBy across the Report collections",
    domain: "reportsUsage",
    srvModule: "reportsUsage",
    priority: "must",
    valueType: "count",
    dims: ["user"],
    rollup: "sum",
    defaultWidget: "hbar",
    altWidgets: ["bar", "table"],
    coveredByV1: false,
    xlsxRow: 89,
  },
  {
    id: "reports_by_module",
    name: "Reports by Module",
    description: "Reports generated within each module.",
    logic: "Group report generation events by module.",
    source: "queue.actionName — a server-set enum, unlike the client-supplied feature",
    domain: "reportsUsage",
    srvModule: "reportsUsage",
    priority: "must",
    valueType: "count",
    dims: ["module"],
    rollup: "sum",
    defaultWidget: "bar",
    altWidgets: ["hbar", "table"],
    coveredByV1: false,
    caveat:
      "The module values are report-feature names taken from the queue's server-set actionName, deliberately not the client-supplied feature field. They are not the same vocabulary as the module dimension in Module Usage, so the two must not be joined on it.",
    xlsxRow: 90,
  },
])
